import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import unifiedApi from '../services/unifiedApi';
import { mapDriverToBackend, mapDriverUpdateToBackend, logMapping } from '../utils/driverMapper.js';
import { toast } from 'sonner';
import logger from '@/utils/logger';

export const DRIVER_STATUS_COLOR = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
  'on-leave': 'bg-yellow-100 text-yellow-800',
};

export const DRIVER_STATUS_LABEL = {
  active: 'Actif',
  inactive: 'Inactif',
  'on-leave': 'En congé',
};

export const getStatusColor = (status) => DRIVER_STATUS_COLOR[status] || 'bg-gray-100 text-gray-800';
export const getStatusLabel = (status) => DRIVER_STATUS_LABEL[status] || 'Inconnu';
export const getLicenseStatusColor = (license) =>
  !license || license.status === 'expired' ? 'text-red-600' : 'text-green-600';

export const useDrivers = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    search: searchParams.get('q') || '',
    status: searchParams.get('status') || 'active',
    route: searchParams.get('route') || '',
  });

  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [driverToEdit, setDriverToEdit] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState(null);

  const {
    data: rawDrivers,
    isPending: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['drivers'],
    queryFn: async () => {
      const driversResponse = await unifiedApi.drivers.getAll();
      return (driversResponse.data || []).map((driver) => ({
        ...driver,
        assigned_bus: driver.assigned_bus ?? null,
        assigned_route: driver.assigned_route ?? null,
        firstName: driver.first_name || driver.firstName || '',
        lastName: driver.last_name || driver.lastName || '',
        licenseNumber: driver.license_number || driver.licenseNumber || '',
        phoneNumber: driver.phone || driver.phoneNumber || '',
        assignedBusId: driver.assigned_bus_id || driver.assignedBusId || null,
        assignedRouteId: driver.assigned_route_id || driver.assignedRouteId || null,
        experience:
          typeof driver.experience === 'object'
            ? driver.experience
            : {
                totalYears: driver.experience || 0,
                previousJobs: [],
                specializations: [],
              },
        rating: driver.rating || 0,
        emergencyContact: driver.emergency_contact || driver.emergencyContact || '',
        address: driver.address || '',
        dateOfBirth: driver.date_of_birth || driver.dateOfBirth || '',
        hireDate: driver.hire_date || driver.hireDate || driver.created_at || '',
      }));
    },
  });

  const error = queryError?.message ?? null;
  const drivers = rawDrivers || [];
  const loadDrivers = refetch;

  const stats = useMemo(
    () => ({
      total: drivers.length,
      active: drivers.filter((d) => d.status === 'active').length,
      inactive: drivers.filter((d) => d.status === 'inactive').length,
      onLeave: drivers.filter((d) => d.status === 'on-leave').length,
      assigned: drivers.filter((d) => d.assigned_bus || d.assigned_route).length,
      averageRating: 0,
      expiredLicenses: 0,
    }),
    [drivers],
  );

  const updateFilters = (newFilters) => {
    const merged = { ...filters, ...newFilters };
    setFilters(merged);
    const params = new URLSearchParams(searchParams);
    if (merged.search) params.set('q', merged.search);
    else params.delete('q');
    if (merged.status) params.set('status', merged.status);
    else params.delete('status');
    setSearchParams(params, { replace: true });
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['drivers'] });

  const createDriver = async (driverData) => {
    try {
      const backendData = mapDriverToBackend(driverData);
      logMapping('create', driverData, backendData, Object.keys(backendData).length);
      const result = await unifiedApi.drivers.create(backendData);
      if (result.success) {
        invalidate();
        return { success: true };
      }
      throw new Error(result.message || 'Erreur lors de la création');
    } catch (err) {
      logger.error('Erreur création chauffeur:', err);
      return {
        success: false,
        error: err.responseData?.data?.error || err.message,
      };
    }
  };

  const updateDriver = async (id, driverData) => {
    try {
      const backendData = mapDriverUpdateToBackend(driverData);
      logMapping('update', driverData, backendData);
      const result = await unifiedApi.drivers.update(id, backendData);
      if (result.success) {
        invalidate();
        return { success: true };
      }
      throw new Error(result.message || 'Erreur lors de la mise à jour');
    } catch (err) {
      logger.error('Erreur mise à jour chauffeur:', err);
      return {
        success: false,
        error: err.responseData?.data?.error || err.message,
      };
    }
  };

  const deleteDriver = async (id) => {
    try {
      const result = await unifiedApi.drivers.delete(id);
      if (result.success) {
        invalidate();
        return { success: true };
      }
      throw new Error(result.message || 'Erreur lors de la suppression');
    } catch (err) {
      logger.error('Erreur suppression chauffeur:', err);
      const errorMessage = err.responseData?.data?.error || err.responseData?.data?.message || err.message;
      return { success: false, error: errorMessage };
    }
  };

  const handleCreateDriver = () => {
    setFormMode('create');
    setDriverToEdit(null);
    setFormModalOpen(true);
  };

  const handleEditDriver = (driver) => {
    setFormMode('edit');
    setDriverToEdit(driver);
    setFormModalOpen(true);
  };

  const handleDeleteDriver = (driver) => {
    setDriverToDelete(driver);
    setDeleteModalOpen(true);
  };

  const confirmDeleteDriver = async () => {
    const driver = driverToDelete;
    setDeleteModalOpen(false);
    setDriverToDelete(null);
    if (!driver) return;

    queryClient.setQueryData(['drivers'], (old) => (old || []).filter((d) => d.id !== driver.id));
    const name = `${driver.first_name || driver.firstName || ''} ${driver.last_name || driver.lastName || ''}`.trim();

    const result = await deleteDriver(driver.id);
    if (!result.success) {
      invalidate();
      toast.error(`Erreur lors de la suppression du chauffeur ${name}`);
    } else {
      toast.success(`Chauffeur ${name} supprimé`);
    }
  };

  const handleSubmitDriver = async (driverData) => {
    setFormLoading(true);
    try {
      if (formMode === 'create') {
        const result = await createDriver(driverData);
        if (result.success) {
          toast.success('Chauffeur créé avec succès !');
          setFormModalOpen(false);
          invalidate();
        } else {
          throw new Error(result.error || 'Erreur lors de la création');
        }
      } else {
        const result = await updateDriver(driverToEdit?.id, driverData);
        if (result.success) {
          toast.success('Chauffeur modifié avec succès !');
          setFormModalOpen(false);
          invalidate();
        } else {
          throw new Error(result.error || 'Erreur lors de la modification');
        }
      }
    } catch (err) {
      logger.error('Erreur lors de la soumission:', err);
      let errorMessage = 'Erreur lors de la sauvegarde du chauffeur';
      if (err.response?.data) {
        errorMessage =
          typeof err.response.data === 'object' && err.response.data.message
            ? err.response.data.message
            : err.response.data;
      } else if (err.message) {
        errorMessage = err.message;
      }
      toast.error(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloseFormModal = () => {
    setFormModalOpen(false);
    setDriverToEdit(null);
  };

  const handleOpenAssignmentModal = (driver) => {
    setSelectedDriver(driver);
    setAssignmentModalOpen(true);
  };

  const handleCloseAssignmentModal = () => {
    setAssignmentModalOpen(false);
    setSelectedDriver(null);
  };

  const handleAssignmentSubmit = async (driverId, busId, routeId) => {
    try {
      await unifiedApi.drivers.assignBusAndRoute(driverId, { busId, routeId });
      setAssignmentModalOpen(false);
      invalidate();
    } catch (err) {
      logger.error('Erreur assignation:', err);
      throw err;
    }
  };

  const filteredDrivers = drivers.filter((driver) => {
    if (!driver) return false;
    const matchesSearch =
      !filters?.search ||
      driver?.firstName?.toLowerCase().includes(filters.search.toLowerCase()) ||
      driver?.lastName?.toLowerCase().includes(filters.search.toLowerCase()) ||
      (driver?.email && driver.email.toLowerCase().includes(filters.search.toLowerCase()));
    const matchesStatus = !filters?.status || driver?.status === filters.status;
    return matchesSearch && matchesStatus;
  });

  return {
    drivers,
    filteredDrivers,
    loading,
    error,
    stats,
    filters,
    assignmentModalOpen,
    selectedDriver,
    formModalOpen,
    formMode,
    driverToEdit,
    formLoading,
    deleteModalOpen,
    driverToDelete,
    updateFilters,
    loadDrivers,
    handleCreateDriver,
    handleEditDriver,
    handleDeleteDriver,
    confirmDeleteDriver,
    setDeleteModalOpen,
    handleSubmitDriver,
    handleCloseFormModal,
    handleOpenAssignmentModal,
    handleCloseAssignmentModal,
    handleAssignmentSubmit,
  };
};

export default useDrivers;
